import { useEffect, useState } from 'react';
import { useMap } from 'react-leaflet';
import { Location } from '../store/realtimeStore';
import L from 'leaflet';
import { ArrowUpIcon } from '@heroicons/react/24/solid';

interface OffScreenIndicator {
  location: Location;
  angle: number;
  distance: number;
  edgeX: number;
  edgeY: number;
}

export function OffScreenIndicators({ locations }: { locations: Location[] }) {
  const map = useMap();
  const [indicators, setIndicators] = useState<OffScreenIndicator[]>([]);

  useEffect(() => {
    const updateIndicators = () => {
      if (!map) return;

      const bounds = map.getBounds();
      const center = map.getCenter();
      const offScreen: OffScreenIndicator[] = [];

      locations.forEach(location => {
        const latLng = L.latLng(location.latitude, location.longitude);
        
        // Check if location is outside viewport
        if (!bounds.contains(latLng)) {
          // Calculate distance in kilometers
          const distance = center.distanceTo(latLng) / 1000;
          
          // Calculate angle from center to location
          const dx = location.longitude - center.lng;
          const dy = location.latitude - center.lat;
          const angle = Math.atan2(dy, dx) * (180 / Math.PI);
          
          // Calculate edge position
          const containerPoint = map.latLngToContainerPoint(center);
          const size = map.getSize();
          const targetAngle = angle * (Math.PI / 180);
          
          // Calculate intersection with viewport edges
          const aspectRatio = size.x / size.y;
          let edgeX, edgeY;
          
          const absAngle = Math.abs(Math.tan(targetAngle));
          
          if (absAngle > aspectRatio) {
            // Intersects top or bottom
            edgeY = targetAngle > 0 ? size.y - 40 : 40;
            edgeX = containerPoint.x + (edgeY - containerPoint.y) / Math.tan(targetAngle);
          } else {
            // Intersects left or right
            edgeX = Math.cos(targetAngle) > 0 ? size.x - 40 : 40;
            edgeY = containerPoint.y + (edgeX - containerPoint.x) * Math.tan(targetAngle);
          }
          
          // Constrain to viewport
          edgeX = Math.max(40, Math.min(size.x - 40, edgeX));
          edgeY = Math.max(40, Math.min(size.y - 40, edgeY));
          
          offScreen.push({
            location,
            angle: angle + 90, // Adjust for arrow rotation
            distance: Math.round(distance * 10) / 10,
            edgeX,
            edgeY,
          });
        }
      });

      setIndicators(offScreen);
    };

    map.on('move', updateIndicators);
    map.on('zoom', updateIndicators);
    updateIndicators();

    return () => {
      map.off('move', updateIndicators);
      map.off('zoom', updateIndicators);
    };
  }, [map, locations]);

  return (
    <>
      {indicators.map((indicator, idx) => {
        const platoon = indicator.location.platoon || indicator.location.device?.platoon;
        const label = platoon?.callsign || 'Platoon';

        return (
          <div
            key={`indicator-${indicator.location.deviceId}-${idx}`}
            className="absolute pointer-events-none z-[1000]"
            style={{
              left: `${indicator.edgeX}px`,
              top: `${indicator.edgeY}px`,
              transform: 'translate(-50%, -50%)',
            }}
          >
            <div className="flex flex-col items-center">
              {/* Arrow pointing to off-screen location */}
              <div
                className="bg-blue-600 rounded-full p-2 shadow-lg border-2 border-white animate-pulse"
                style={{
                  transform: `rotate(${indicator.angle}deg)`,
                }}
              >
                <ArrowUpIcon className="w-4 h-4 text-white" />
              </div>
              
              {/* Name and distance label */}
              <div className="mt-1 bg-gray-900/90 px-2 py-1 rounded-full text-white text-xs font-bold whitespace-nowrap shadow-lg border border-gray-700">
                {label}
                <span className="ml-1 text-blue-400">{indicator.distance}km</span>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}
